/**
 *  Plan store module file (Pinia)
 *
 *  Idiomatic Pinia option store: getters are pure `(state) => value` functions
 *  (see ./getters), and former Vuex mutations are folded into `this`-based
 *  actions (camelCase names). Former Vuex actions are also camelCase.
 *  The ACTIONS/GETTERS/MUTATIONS constant maps are deleted; consumers call
 *  store.actionName() / storeToRefs(store).getterName directly.
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore } from 'pinia'
import { state, MODEL_PLAN, MODEL_ASSETS, MODEL_SUBPLAN_FILTERS } from './state'
import { getters as planGetters } from './getters'

import type { Plan } from '@workspaces/types'
import type { AssetDataGeoJsonLayer } from '@/plan/filter/asset.type'
import layerServiceC3 from '@/layers/layerService'
import {
  ASSETS,
  CLUSTER_ASSETS,
  EXCLUDED_ASSETS,
  EXCLUDED_CLUSTER_ASSETS,
} from '@/layers'
import {
  deserializeFilters,
  deserializeLegend,
} from '@/plan/deserializeFilters'
import {
  filterAssetsFromPlan,
  getUnfilteredElements,
  getPlanFiltersFromStorage,
} from '@/helpers/plan.helper'
import PlanService from '@/services/plan.service'
import CustomGeoboundary from '@/services/custom-geoboundary.service'
import { getAppMetadata } from '@/plan/metadata/metadata'
import { managePoisLayerVisibility } from '@/helpers/pois.helper'
import { manageBoundariesLayerVisibility } from '@/helpers/boundaries.helper'
import { getPOIsByTypesFromFilter } from '@/plan/filter/pois.helper'
import {
  getZoomLevelForDeclustering,
  isAudiencesQuintileEnabled,
  isIntelligentMediaTypeDigitalStaticEnabled,
} from '@/plan/metadata/metadata.helper'
import { shouldAssetSimpleLayerBeVisible } from '@/helpers/map.helper'
import { getImpressionsForAssets } from '@/plan/filter/asset.helper'
import sharedPlanService from '@/services/sharedPlan.service'
import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { serializeLegend } from '@/utils/serializeLegend'
import { getInstance as getAuth0ServiceIntance } from '@/auth/index'
import AssetsService from '@/services/assets-browser.service'
import { sleep } from '@/helpers/common.helper'
import { setupAudiencesQuintileLayer } from '@/helpers/audiences-quintile.helper'
import {
  isAudiencesQuintileInFilter,
  isGeoboundaryFilterReadyForAudiencesQuintile,
} from '@/plan/filter/audiences-quintile.helper'
import { analyzeAssetsForAllDigitalOrAllClassic } from '@/helpers/media-type.helper'
import { POIsRampColor, POIsOthersColor } from '@/layers/pois/pois-layer.helper'
import { areEquals } from '@/utils/arrayUtils'
import { updatePOIsFilterCache } from '@/layers/pois/pois-filter-cache'
import { deepToRaw } from '@/utils/deepToRaw'
import type { PlanState, ProximityFlags, SubPlanFilter } from './state.type'
import type { Filter, SimplePlanWithFilters } from '@/plan/filter/filter.type'
import type {
  FilterByPoisParamsRequestData,
  PoisFilter,
} from '@/plan/filter/pois.type'

/**
 * Working shape for a plan being loaded/prepared. It starts as the MODEL_PLAN
 * shape and is progressively merged with the raw fetched plan (which carries
 * Firestore-style timestamps and a possibly-serialized `filters` string), hence
 * the permissive optional fields and index signature.
 */
type LoadedPlan = {
  loading?: boolean
  dirty?: boolean
  filters?: SubPlanFilter[] | string
  legend?: unknown
  poisColors?: string | Record<string, unknown>
  created?: number | Date
  created_at?: number | Date | { _seconds: number }
  modified?: number | Date
  modified_at?: number | Date | { _seconds: number }
  [key: string]: unknown
}

const APP_METADATA = getAppMetadata()
const MODULE_NAME = 'plan'

export const usePlanStore = defineStore(MODULE_NAME, {
  state: (): PlanState => state,
  getters: planGetters,
  actions: {
    // ─── Former mutations folded into this-based actions ────────────────────
    savePlan(payload: any = {}) {
      this.plan = payload
      this.plan_backup = structuredClone(deepToRaw(this.plan))

      const rawPoisColors = payload.poisColors ?? '{}'
      let poisColors = rawPoisColors
      if (typeof rawPoisColors === 'string') {
        poisColors = JSON.parse(rawPoisColors)
      }
      this.poisColors = poisColors
    },
    savePlanFilter(
      payload: {
        filters: any[]
        updateDirtyFlags?: boolean
      } = { filters: [], updateDirtyFlags: true },
    ) {
      // Replicate first subfilters to all filters: Proximity, Geographical distribution
      const payloadModified = payload.filters.map((item, index) => {
        if (index !== this.activeSubPlanFiltersIndex) {
          // proximity
          item.proximity = {
            ...payload.filters[this.activeSubPlanFiltersIndex].proximity,
          }
          // geographical distribution
          if (
            this.plan.filters[this.activeSubPlanFiltersIndex]
              .geographical_distribution
          ) {
            item.geographical_distribution = {
              ...payload.filters[this.activeSubPlanFiltersIndex]
                .geographical_distribution,
            }
          }
        }
        return item
      })
      // Set the new filters
      this.plan.filters = payloadModified
      // set custom geoboundaries to the plan
      const customGeoboundariesIds = payload.filters.reduce<string[]>(
        (acc, filter) => {
          const geoboundaries = filter.geoboundaries as
            | Record<string, Array<{ id: string }>>
            | undefined
          if (geoboundaries) {
            const region16Ids = geoboundaries.region_16?.map((r) => r.id) || []
            const region17Ids = geoboundaries.region_17?.map((r) => r.id) || []
            acc = [...acc, ...region16Ids, ...region17Ids]
          }
          return acc
        },
        [],
      )
      const uniqueCustomGeoboundaryIds = [...new Set(customGeoboundariesIds)]
      this.plan.custom_geoboundaries = uniqueCustomGeoboundaryIds || []
      // set custom pois to the plan
      const customPoiIds = payload.filters.reduce<string[]>((acc, filter) => {
        const proximity = filter.proximity
        if (proximity && proximity.data) {
          const data = proximity.data as Array<{ type: string; id: string }>
          const ids =
            data.filter((r) => r.type === 'custom').map((r) => r.id) || []
          acc = [...acc, ...ids]
        }
        return acc
      }, [])
      const uniqueCustomPoiIds = [...new Set(customPoiIds)]
      this.plan.custom_pois = uniqueCustomPoiIds || []

      // Update dirty property
      this.plan.dirty = payload.updateDirtyFlags
        ? payload.updateDirtyFlags
        : true
      // Update plan backup
      this.plan_backup = structuredClone(deepToRaw(this.plan))
    },
    modifyPlanSubfilterTabname(
      payload: { id?: number | string; tabname?: string } = {},
    ) {
      this.plan.filters = [...this.plan.filters].map((item, index) => {
        if (index === payload.id) {
          item.tabname = payload.tabname
        }
        return item
      })
      this.plan_backup = structuredClone(deepToRaw(this.plan))
    },
    setPlanLoading(payload: any) {
      this.plan.loading = payload
    },
    setGeographicalDistributionRegions(payload: any) {
      this.geographicalDistributionRegions = payload || []
    },
    setIsLoadingGeographicalDistributionRegions(payload: any) {
      this.loadingGeographicalDistributionRegions = payload || false
    },
    setAssetsLoading(payload: any) {
      this.assets.loading = payload
    },
    setAssetsInitialStats(payload: any) {
      const impressions = payload.allAssetsImpressions ?? -1
      // NOTE: reads `this.meta` (not `this.assets.meta`); preserved as-is — at
      // runtime `this.meta` is undefined so this branch is always taken.
      if (
        !(this as { meta?: { stats?: { impressions?: number } } }).meta?.stats
          ?.impressions
      ) {
        this.assets.meta = {
          stats: { allAssetsImpressions: impressions, impressions },
        }
      } else {
        // The if-branch above proves `stats` exists in this branch.
        const stats = this.assets.meta.stats as NonNullable<
          typeof this.assets.meta.stats
        >
        stats.allAssetsImpressions = impressions
        stats.impressions = impressions
        this.plan.impressions = impressions
      }
    },
    setAssetsTotalStats(payload: any) {
      this.assets.count = payload.count ?? -1
      if (!this.assets.meta.stats) {
        this.assets.meta.stats = {}
      }
      this.assets.meta.stats.impressions = payload.impressions ?? -1
      this.plan.impressions = payload.impressions ?? -1
    },
    setSubplanFiltersIndex(payload: any) {
      this.activeSubPlanFiltersIndex = payload
    },
    setDefaultUserCountries(payload: any) {
      this.defaultUserCountries = payload
    },
    addSubplanFilters() {
      // add a new subplan filters
      this.plan.filters = [...this.plan.filters, MODEL_SUBPLAN_FILTERS()]
      // copy countries to the new subplan filters
      this.plan.filters[this.plan.filters.length - 1].countries = [
        ...this.plan.filters[0].countries,
      ]
      // copy proximity to the new subplan filters
      this.plan.filters[this.plan.filters.length - 1].proximity = {
        ...this.plan.filters[0].proximity,
      }
      // copy audience to the new subplan filters
      this.plan.filters[this.plan.filters.length - 1].delivery =
        this.plan.filters[0].delivery
      // copy geographical distribution to the new subplan filters
      if (this.plan.filters[0].geographical_distribution) {
        this.plan.filters[
          this.plan.filters.length - 1
        ].geographical_distribution = {
          ...this.plan.filters[0].geographical_distribution,
        }
      }
      // define active the new subplan filters
      this.activeSubPlanFiltersIndex = this.plan.filters.length - 1
      // Update dirty property
      this.plan.dirty = true
      // Update plan backup
      this.plan_backup = structuredClone(deepToRaw(this.plan))
    },
    removeSubplanFilters(payload: any) {
      if (this.plan.filters.length > 1) {
        const filters = [...this.plan.filters].filter(
          (item, index) => index !== payload,
        )
        this.plan.filters = [...filters]
        this.plan.dirty = true
        this.plan_backup = structuredClone(deepToRaw(this.plan))
      }
    },
    setAssets(payload: any) {
      this.assets = {
        count: payload.count,
        meta: payload.meta || this.assets.meta,
        data: payload.results || [],
        page: payload.page,
        page_size: payload.page_size,
        loading: false,
      }
    },
    resetPlan() {
      this.plan = MODEL_PLAN()
      this.assets = MODEL_ASSETS()
    },
    setAssetsFinishedLoad(payload: any) {
      this.assetsFinishedLoad = payload
    },
    restorePlanFromBackup() {
      this.plan = structuredClone(deepToRaw(this.plan_backup))
    },
    setOldDirtyPlan() {
      const { filters, previousRoute } = getPlanFiltersFromStorage()
      if (
        previousRoute &&
        previousRoute.includes('/insights') &&
        previousRoute.includes('/panel') &&
        filters !== null
      ) {
        const preparedPlan = { ...this.plan }
        // Stored Filter[] is structurally compatible with SubPlanFilter[].
        preparedPlan.filters = filters as unknown as SubPlanFilter[]
        preparedPlan.dirty = true
        console.debug('🟪 Plan: Restoring an old dirty plan')
        this.plan = preparedPlan
      }
    },
    setViewstate(payload: any) {
      this.viewState = payload
    },
    setMapupdateflags(payload: any) {
      this.mapUpdateFlags = { ...this.mapUpdateFlags, ...payload }
    },
    setProximityflags({
      key,
      id,
      value,
    }: {
      key: string
      id: string
      value: unknown
    }) {
      // Vue 3 reactivity tracks plain property assignment (no Vue.set needed).
      ;(
        this.proximityFlags as unknown as Record<
          string,
          Record<string, unknown>
        >
      )[key][id] = value
    },
    resetProximityflags() {
      this.proximityFlags = {
        open: {},
        selected: {},
        halfSelected: {},
        hidden: {},
      }
    },
    removeCustomGeometry(_ids?: any[]) {
      this.fixme = null
    },
    setOpenGeometry(id: any) {
      this.openCustomGeometry = id
    },
    setNewPoiDatasetForm(payload: any) {
      this.newPoiDatasetForm = {
        ...this.newPoiDatasetForm,
        ...payload,
      }
    },
    setDistribution(payload: any = {}) {
      this.distribution = payload
    },
    setAudienceDelivery(delivery: any) {
      this.latestdelivery = delivery
    },
    updateProximityData(payload: any) {
      this.plan.proximity = payload
      // Update POIs filter cache when proximity data changes.
      updatePOIsFilterCache(
        this.plan as unknown as Parameters<typeof updatePOIsFilterCache>[0],
      )
    },
    updateLegendFilter(payload: any) {
      this.plan.legend = {
        ...this.plan.legend,
        ...payload,
      }
    },
    setExcludedAssetLayerAvailable(payload: any) {
      this.plan.legend.canAssetsExcludedLayerBeShown = payload
    },
    setAssetsDetails(payload: any) {
      this.assetsDetails = payload
    },
    setAssetsReadyWithAllProperties(payload: any) {
      this.assetsReadyWithAllProperties = payload.ready
    },
    setAssetsFacingOrientationIcons(payload: any) {
      this.plan.legend.facingOrientationIcons = payload
    },
    setPoisBufferVisibility(payload: any) {
      this.plan.legend.poisBufferVisibility = payload
    },
    setPoisVisibility(payload: any) {
      this.plan.legend.poisVisibility = payload
    },
    setBoundariesVisibility(payload: any) {
      this.plan.legend.boundariesVisibility = payload
    },
    setAssetsIdsLayerVisibility(payload: any) {
      this.plan.legend.assetsIdsLayerVisibility = payload
    },
    setColorizeAssets(payload: any) {
      this.plan.legend.colorizeAssets = payload
    },
    setBasemap(payload: any) {
      this.plan.basemap = payload
    },
    setForceLogout(payload: any) {
      this.forceLogout = payload
    },
    setPlanName(payload: any) {
      this.plan.name = payload
    },
    setPlanDescription(payload: any) {
      this.plan.description = payload
    },
    setPlanClient(payload: any) {
      this.plan.client = payload
    },
    setPlanBrand(payload: any) {
      this.plan.brand = payload
    },
    setPlanVisibility(payload: any) {
      this.plan.is_public = payload
    },
    setHighlightedAssets(payload: any) {
      this.highlightedAssets = payload
    },
    setPoiFreeColor(payload: any) {
      const poisColors = { ...this.poisColors }
      const colorForPOI = poisColors[payload]
      if (colorForPOI === undefined) {
        let assignedColor = POIsOthersColor
        const assignedColors = Object.values(
          JSON.parse(JSON.stringify(this.poisColors)),
        ) as number[][]
        const availableColors = POIsRampColor.filter((color) => {
          return !assignedColors.find((assignedColorsElement) =>
            areEquals(assignedColorsElement, color as unknown as number[]),
          )
        })
        if (availableColors.length > 0) {
          assignedColor = availableColors[0]
        }
        poisColors[payload] = assignedColor
        this.poisColors = poisColors
      }
    },
    clearPoiColor(payload: any) {
      const poisColors = { ...this.poisColors }
      const colorToBeRemoved = poisColors[payload]
      if (colorToBeRemoved === undefined) {
        throw new Error(`POI ${payload} has no color associated`)
      }

      delete poisColors[payload]
      this.poisColors = poisColors
    },
    clearPoisColor() {
      this.poisColors = {}
    },
    setPlanDirty(_dirty = true) {
      this.plan.dirty = true
    },
    setAssetsAngleNorthMap(payload: any) {
      this.plan.legend.angleNorthMap = payload
    },
    setAudiencesQuintileVisibility(payload: any) {
      this.plan.legend.audiencesQuintile = payload
    },
    setAssetsDigitalStaticProperties(payload: any) {
      this.assetsProperties = payload
    },
    addGeoboundaryGeometryToCache(payload: any) {
      console.debug('🌐 Saving to geoboundaries cache:', payload)
      const found = this.geoBoundariesGeometriesCache.find(
        (element) =>
          element.id === payload.id && element.type_id === payload.type_id,
      )
      if (found) return
      if (this.geoBoundariesGeometriesCache.length < 20) {
        this.geoBoundariesGeometriesCache.push(payload)
      } else {
        this.geoBoundariesGeometriesCache.shift()
        this.geoBoundariesGeometriesCache.push(payload)
      }
      console.debug(
        '🌐 Cache content: Geoboundaries geometries',
        this.geoBoundariesGeometriesCache.map((g) => `${g.id} (${g.type_id})`),
      )
    },
    setMyPlans(payload: any) {
      this.plans.user = payload
    },
    setPublicPlans(payload: any) {
      this.plans.public = payload
    },
    setIsSubscribedToPlans(payload: any) {
      this.plans.subscribed = payload
    },
    setEditingPlan(payload: any) {
      this.plan.editing = payload
    },
    setAiAgentWaitingResponse(payload: any) {
      this.aiAgent.waitingResponse = payload
    },

    // ─── Former Vuex actions (async, now camelCase) ─────────────────────────
    async loadPlan({ id, sharedPlan, password }: any) {
      this.setPlanLoading(true)
      const originalUrl = window.location.href

      try {
        const defaultPlan = MODEL_PLAN()
        const defaultSubPlanFilters = MODEL_SUBPLAN_FILTERS()
        let plan: LoadedPlan = MODEL_PLAN()
        if (sharedPlan) {
          console.debug('🟪 Shared Plan: Fetching plan properties...')
          const planFetched = await sharedPlanService.getPlan(
            APP_METADATA,
            BrowserEnvironmentResolver.getInstance(),
            id,
            password,
          )

          plan = { ...plan, ...planFetched }

          if (typeof plan.filters === 'string') {
            plan.filters = deserializeFilters(plan.filters)
            // created_at/modified_at arrive as Firestore Timestamps ({ _seconds })
            plan.created_at =
              (plan.created_at as { _seconds: number })._seconds * 1000
            const modifiedSeconds = (
              plan.modified_at as { _seconds: number } | undefined
            )?._seconds
            // Preserve original behavior: undefined * 1000 === NaN
            plan.modified_at = (modifiedSeconds as number) * 1000
          }

          /**
           * TODO: I have no idea why do not have a deserializer for proximity filter params
           * In case no proximity filter is set we need to do this for shared plans
           **/
          for (const filterTabs of plan.filters as SubPlanFilter[]) {
            if (!filterTabs.proximity) {
              filterTabs.proximity = { data: [] }
            }
          }
        } else {
          console.debug('🟪 Plan: Fetching...')
          // Plan.Plan (an interface without an index signature) is structurally
          // the loaded plan; cast to the permissive working type.
          plan = (await PlanService.getPlan(
            APP_METADATA,
            BrowserEnvironmentResolver.getInstance(),
            id,
          )) as unknown as LoadedPlan

          console.debug('🟪 Plan: Preparing filters...')
          const deserializedFilters = deserializeFilters(plan.filters)
          const filters = deserializedFilters.map(
            (subfilter: Partial<SubPlanFilter>) => {
              return { ...defaultSubPlanFilters, ...subfilter }
            },
          )
          plan.filters = filters

          console.debug('🟪 Plan: Preparing attributes...')
          const rawPoisColors = (plan.poisColors as string | undefined) ?? '{}'
          const poisColors = JSON.parse(rawPoisColors)
          plan.poisColors = poisColors
        }

        const preparedPlan = {
          ...defaultPlan,
          ...plan,
          dirty: false,
          created: plan.created_at,
          modified: plan.modified_at || plan.created_at,
          legend: deserializeLegend(plan.legend as Plan.Legend),
        }

        // load dirty plan (going back from insights panel)
        if (!sharedPlan) {
          const { filters, previousRoute } = getPlanFiltersFromStorage()
          if (
            previousRoute &&
            previousRoute.includes('/insights') &&
            previousRoute.includes('/panel') &&
            filters !== null
          ) {
            // Stored Filter[] is structurally compatible with SubPlanFilter[].
            preparedPlan.filters = filters as unknown as SubPlanFilter[]
            preparedPlan.dirty = true
            console.debug(
              '🟪 Plan: Coming from Insights, reloading old dirty plan',
            )
          }
        }

        console.debug('🟪 Plan: Saving plan in the state...')
        this.savePlan(preparedPlan)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[actions.js] Error in LOAD_PLAN', err)
        if (sharedPlan) {
          const currentUrl = window.location.href
          const parameters = currentUrl.split('?')[1]
          const sharedPlanFeedbackUrl = `/shared-plan-feedback?${parameters}&type=password&link=${currentUrl}`
          window.location.href = sharedPlanFeedbackUrl
        } else {
          const planNotFoundUrl = `/plan-not-found?link=${encodeURIComponent(
            originalUrl,
          )}`
          window.location.href = planNotFoundUrl
        }
      } finally {
        this.setPlanLoading(false)
      }
    },
    async loadCustomGeoboundary({ id }: any) {
      this.setPlanLoading(true)

      try {
        const defaultPlan = MODEL_PLAN()
        const defaultSubPlanFilters = MODEL_SUBPLAN_FILTERS()
        console.debug('      👉 Fetching custom geoboundary dataset')
        const dataset = await CustomGeoboundary.getCustomGeoboundary(
          APP_METADATA,
          BrowserEnvironmentResolver.getInstance(),
          id,
        )
        console.debug('         OK - Fetching custom geoboundary dataset')
        const deserializedFilters = deserializeFilters(dataset.filters)
        const filters = deserializedFilters.map(
          (subfilter: Partial<SubPlanFilter>) => {
            return { ...defaultSubPlanFilters, ...subfilter }
          },
        )

        const preparedDataset = {
          ...defaultPlan,
          ...dataset,
          dirty: false,
          filters,
          created: dataset.created_at,
          modified: dataset.modified_at || dataset.created_at,
        }

        this.savePlan(preparedDataset)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[actions.js] Error in LOAD_CUSTOM_GEOBOUNDARY', err)
        const response = (err as { response?: { status?: number } }).response
        const fromAPI = response && (response.status ?? 0) >= 400
        window.alert(
          `${
            fromAPI ? 'API' : ''
          } Error loading plan. Check the browser console for more details`,
        )
      } finally {
        this.setPlanLoading(false)
      }
    },
    async createPlan(payload: any) {
      const planFormatted = {
        ...payload,
        filters: JSON.stringify(payload.filters),
        legend: serializeLegend(payload.legend),
      }
      const id = await PlanService.createPlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        planFormatted,
      )
      const auth0Instance = getAuth0ServiceIntance()
      const userId = auth0Instance.getUser()
      const plan = { ...payload, created_by: userId, id, modified: new Date() }
      this.savePlan({
        ...plan,
        dirty: false,
        filters: this.plan.filters,
      })
      const { categories, brands, locations, tags } = getPOIsByTypesFromFilter(
        this.plan.filters[this.activeSubPlanFiltersIndex].proximity
          .data as Parameters<typeof getPOIsByTypesFromFilter>[0],
      )
      this.updateProximityData({
        categories,
        brands,
        locations,
        tags,
      })
      return plan
    },
    async createCustomGeoboundary(payload: any) {
      const datasetFormatted = {
        ...payload.dataset,
        filters: JSON.stringify(payload.dataset.filters),
      }
      const userId = getAuth0ServiceIntance().getUser()
      const id = await CustomGeoboundary.createCustomGeoboundary(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        userId as string,
        datasetFormatted,
      )
      const dataset = { ...payload.dataset, id }
      if (!payload.refresh) {
        return dataset
      }
      this.savePlan({
        ...dataset,
        dirty: false,
        filters: this.plan.filters,
      })
      return dataset
    },
    async updatePlan(payload: any) {
      const plan = {
        ...payload.plan,
        filters: JSON.stringify(payload.plan.filters),
        legend: serializeLegend(payload.plan.legend),
      }

      if (typeof plan.poisColors !== 'string') {
        if (!plan.poisColors) {
          plan.poisColors = {}
        }
        plan.poisColors = JSON.stringify(plan.poisColors)
      }

      await PlanService.updatePlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload.id,
        plan,
      )
      this.savePlan({
        ...plan,
        modified: new Date(),
        dirty: false,
        filters: this.plan.filters,
      })
      const { categories, brands, locations, tags } = getPOIsByTypesFromFilter(
        this.plan.filters[this.activeSubPlanFiltersIndex].proximity
          .data as Parameters<typeof getPOIsByTypesFromFilter>[0],
      )
      this.updateProximityData({
        categories,
        brands,
        locations,
        tags,
      })
    },
    async updateCustomGeoboundary(payload: any) {
      const dataset = {
        ...payload.plan,
        filters: JSON.stringify(payload.plan.filters),
      }
      await CustomGeoboundary.updateCustomGeoboundary(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload.id,
        dataset,
      )
      this.savePlan({
        ...dataset,
        dirty: false,
        filters: this.plan.filters,
      })
    },
    async deletePlan({ id }: any) {
      return PlanService.deletePlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        id,
      )
    },
    async deleteCustomGeoboundary({ id }: any) {
      return CustomGeoboundary.deleteCustomGeoboundary(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        id,
      )
    },
    async loadPoisLayer() {
      try {
        this.setMapupdateflags({ pois: false })
      } catch (err) {
        console.error('[actions.js] Error in LOAD_POIS_LAYER', err)
        const response = (err as { response?: { status?: number } }).response
        const fromAPI = response && (response.status ?? 0) >= 400
        window.alert(
          `${
            fromAPI ? 'API' : ''
          } Error updating POIs layer. Check the browser console for more details`,
        )
      }
    },
    async updateAssetsInitialStats(params: any = {}) {
      this.setAssetsInitialStats(params)
    },
    async updateAssetLayer(params: any = {}) {
      let needToGetSecondaryPropertiesForAssets = false
      const plan = this.getPlan
      if (params.doNotEmitBusEvent === undefined) {
        params.doNotEmitBusEvent = false
      }

      const flyToExtent = params.flyToExtent ?? true

      this.setAssetsLoading(true)

      let allAssets: AssetDataGeoJsonLayer[] = []

      const retiresForWaittingIndexDdInitialization = 5
      const timeBetweenRetries = [0, 100, 200, 300, 400]
      let retries = 0
      let successfullyFetchedAssets = false

      // Getting assets

      while (
        retries < retiresForWaittingIndexDdInitialization &&
        !successfullyFetchedAssets
      ) {
        await sleep(timeBetweenRetries[retries])
        try {
          console.debug(
            `🟪 Plan: 🔁 Fetching Assets: Attempt: ${retries + 1} || Assets: ${
              allAssets.length
            }`,
          )
          if (allAssets.length === 0) {
            const { allGeoJsonAssets, needToGetSecondaryProperties } =
              await AssetsService.fetchLayerV2(
                getAppMetadata(),
                BrowserEnvironmentResolver.getInstance(),
                plan.filters as unknown as Filter[],
              )
            needToGetSecondaryPropertiesForAssets = needToGetSecondaryProperties
            console.debug(
              `🟪 Plan: 🐹 Secondary assets properties needed: ${needToGetSecondaryProperties}`,
            )
            allAssets = allGeoJsonAssets.data
              .features as unknown as AssetDataGeoJsonLayer[]
            this.setAssetsReadyWithAllProperties({
              ready: !needToGetSecondaryProperties,
            })
          }
          successfullyFetchedAssets = true
        } catch (err) {
          console.error(
            '   🔴 Vuex - actions.js - UPDATE_ASSET_LAYER - An error occur while trying to fetch assets',
            err,
          )
          retries++
          successfullyFetchedAssets = false
        }
      }
      if (!successfullyFetchedAssets) {
        console.warn(
          '🔴 🔴 Could not set assets into indexBD, seems indexDB is not ready ... 🔴 🔴',
        )
        this.setMapupdateflags({ assets: false })
        this.setAssetsLoading(false)
        return
      }
      if (retries > 2) {
        console.warn(
          `🟠 Took ${
            retries + 1
          } attempts to correctly fetch assets, it would be a good idea to take a look why`,
        )
      }
      console.debug(
        `🟪 Plan: 🔁 Fetched Assets: Attempt: ${retries + 1} || Assets: ${
          allAssets.length
        }`,
      )

      // To avoid race condition if data for latest delivery is not ready
      let retriesLatestDelivery = 0
      let latestDelivery = this.getAudienceDelivery
      if (!latestDelivery) {
        console.debug(
          `🟪 Plan: 🔁 Fetching audience delivery: Latest: ${latestDelivery}`,
        )
      }
      while (!latestDelivery && retriesLatestDelivery < 5) {
        await sleep(Math.pow(2, retriesLatestDelivery) * 100)
        latestDelivery = this.getAudienceDelivery
        retriesLatestDelivery++
      }
      if (retriesLatestDelivery > 0) {
        console.debug(
          `🟪 Plan: 🔁 Fetching audience delivery: Attempts: ${retriesLatestDelivery}`,
        )
      }
      console.debug(
        `🟪 Plan: 🔁 Fetched audience delivery: Latest: ${latestDelivery}`,
      )

      const generateAuditEvent =
        params.generateAuditEvent === undefined
          ? true
          : params.generateAuditEvent
      console.debug('📢 Audit: Generating Event', generateAuditEvent)
      console.debug('🟪 Plan: 🔍 Filtering assets...')
      let assetsToShow = await filterAssetsFromPlan(
        allAssets,
        plan as unknown as SimplePlanWithFilters,
        latestDelivery as string,
        generateAuditEvent,
      )

      // Update assets count and impressions
      const impressions = getImpressionsForAssets(assetsToShow)
      this.setAssetsTotalStats({
        count: assetsToShow.filter((asset) => !asset.properties.excluded)
          .length,
        impressions,
      })

      const activeSubPlanFiltersIndex = this.getSubplanFiltersIndex
      const currentSubFilter = plan.filters[activeSubPlanFiltersIndex]

      // manage layers visibility
      manageBoundariesLayerVisibility(
        plan.legend as unknown as Plan.Legend,
        false,
      )
      managePoisLayerVisibility(
        plan.legend as unknown as Plan.Legend,
        currentSubFilter.proximity as unknown as PoisFilter,
        false,
      )
      layerServiceC3.updateDeckInstance()

      // Update proximity data
      const { categories, brands, locations, tags } = getPOIsByTypesFromFilter(
        (currentSubFilter.proximity.data ??
          []) as FilterByPoisParamsRequestData[],
      )
      this.updateProximityData({
        categories,
        brands,
        locations,
        tags,
      })

      this.setExcludedAssetLayerAvailable(
        assetsToShow.length < allAssets.length,
      )
      const mapZoom = layerServiceC3.getZoom()
      const declusteringZoomLevel = getZoomLevelForDeclustering(APP_METADATA)
      const assetSimpleLayerShouldBeVisible = shouldAssetSimpleLayerBeVisible(
        declusteringZoomLevel,
        mapZoom,
      )
      const assetsLayerVisibility = assetsToShow.length > 0

      const secondaryPropertiesReady = this.getAssetsReadyWithAllProperties
      console.debug('       All assets properties are ready now? ', {
        needToGetSecondaryPropertiesForAssets,
        secondaryPropertiesReady,
      })
      if (needToGetSecondaryPropertiesForAssets && secondaryPropertiesReady) {
        const { allGeoJsonAssets } = await AssetsService.fetchLayerV2(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
          plan.filters as unknown as Filter[],
        )
        allAssets = allGeoJsonAssets.data
          .features as unknown as AssetDataGeoJsonLayer[]

        const assetsMap = new Map()
        allAssets.forEach((asset) => {
          assetsMap.set(asset.properties.id, asset)
        })
        const assetsToShowFullProperties = assetsToShow.map((asset) => {
          return assetsMap.get(asset.properties.id)
        })
        assetsToShow = assetsToShowFullProperties
        console.debug('        Re-fetched assets properties')
      }

      const isAudiencesQuintileFeatureEnabled =
        isAudiencesQuintileEnabled(APP_METADATA)
      const isAudiencesQuintileEnableInLegend =
        this.getAudiencesQuintileVisibility
      const geoboundaryFiltersAreSetOK =
        isGeoboundaryFilterReadyForAudiencesQuintile(
          plan.filters[0] as unknown as Filter,
        )
      const audiencesQuintileFiltersAreSetOK = isAudiencesQuintileInFilter(
        plan.filters[0] as unknown as Filter,
      )
      if (
        isAudiencesQuintileFeatureEnabled &&
        isAudiencesQuintileEnableInLegend &&
        geoboundaryFiltersAreSetOK &&
        audiencesQuintileFiltersAreSetOK
      ) {
        // NOTE: the 4th `isSharedPlan` argument is omitted here (resolves to
        // `undefined` at runtime); preserved as-is via a cast to a 3-arg signature.
        const setupAudiencesQuintileLayerLoose =
          setupAudiencesQuintileLayer as unknown as (...args: unknown[]) => void
        setupAudiencesQuintileLayerLoose(
          APP_METADATA,
          BrowserEnvironmentResolver.getInstance(),
          plan,
        )
      }

      layerServiceC3.updateLayer(ASSETS, {
        ...params,
        data: allAssets,
        assetsToShow,
        visible: assetsLayerVisibility && assetSimpleLayerShouldBeVisible,
        flyToExtent,
        secondaryPropertiesReady,
      })
      layerServiceC3.updateLayer(CLUSTER_ASSETS, {
        ...params,
        data: allAssets,
        assetsToShow,
        visible: assetsLayerVisibility && !assetSimpleLayerShouldBeVisible,
        flyToExtent,
        secondaryPropertiesReady,
      })
      // Legend
      const legend = this.getLegendFilter
      if (legend.excludeAssets) {
        const assetsExcluded = getUnfilteredElements(allAssets, assetsToShow)
        layerServiceC3.updateLayer(EXCLUDED_CLUSTER_ASSETS, {
          assetsToShow: assetsExcluded,
          ...params,
          visible: !assetSimpleLayerShouldBeVisible,
        })
        layerServiceC3.updateLayer(EXCLUDED_ASSETS, {
          assetsToShow: assetsExcluded,
          ...params,
          visible: assetSimpleLayerShouldBeVisible,
        })
      } else {
        layerServiceC3.hideLayer(EXCLUDED_ASSETS)
        layerServiceC3.hideLayer(EXCLUDED_CLUSTER_ASSETS)
      }

      this.setMapupdateflags({ assets: false })
      this.setAssetsLoading(false)

      // When this update does not fly the camera (e.g. excluding/including
      // assets), deck.gl 9's idle AnimationLoop never draws a frame on its own,
      // so the layer changes above stay unrendered (stale icons). A flyTo drives
      // its own frames, so only force a redraw on the no-flyTo path.
      if (!flyToExtent) {
        layerServiceC3.forceIdleRedraw()
      }

      if (isIntelligentMediaTypeDigitalStaticEnabled(APP_METADATA)) {
        const assetsProperties = analyzeAssetsForAllDigitalOrAllClassic(
          assetsToShow as unknown as Parameters<
            typeof analyzeAssetsForAllDigitalOrAllClassic
          >[0],
        )
        this.setAssetsDigitalStaticProperties(assetsProperties)
      }

      console.debug('🟢 Updating Assets Layer - OK')
    },
    updateAudienceDelivery(delivery: any) {
      this.setAudienceDelivery(delivery)
    },
    updateAssetsFacingOrientationIcons(enabled: any) {
      this.setAssetsFacingOrientationIcons(enabled)
    },
    updatePoisBufferVisibility(enabled: any) {
      this.setPoisBufferVisibility(enabled)
    },
    updatePoisVisibility(enabled: any) {
      this.setPoisVisibility(enabled)
    },
    updateBoundariesVisibility(enabled: any) {
      this.setBoundariesVisibility(enabled)
    },
    updateAudiencesQuintileVisibility(enabled: any) {
      this.setAudiencesQuintileVisibility(enabled)
    },
    updateAssetsIdsLayerVisibility(enabled: any) {
      this.setAssetsIdsLayerVisibility(enabled)
    },
    updateColorizeAssets(enabled: any) {
      this.setColorizeAssets(enabled)
    },
    async updatePlanFilter(payload: any) {
      /*
      Payload:
         - filters: Array of filters
         - flagToSetDirty?: must be ['assets', 'pois', 'geoboundaries']
      */

      const filtersUpdated = payload.filters
      if (!filtersUpdated) {
        throw new Error('Filters are required')
      }
      if (!Array.isArray(filtersUpdated)) {
        throw new Error('Filters must be an array')
      }
      if (filtersUpdated.length === 0) {
        throw new Error('Filters cannot be empty')
      }

      const validFlags = ['assets', 'pois', 'geoboundaries']
      const flagToSetDirty = payload.flagToSetDirty
      if (flagToSetDirty && !validFlags.includes(flagToSetDirty)) {
        // NOTE: Error only takes (message, options); the second arg is ignored at
        // runtime. Preserved as-is; cast satisfies the Error signature.
        throw new Error(
          'Invalid flag to set dirty, available values are: ',
          validFlags as unknown as ErrorOptions,
        )
      }

      this.savePlanFilter({
        filters: filtersUpdated,
      })

      if (flagToSetDirty) {
        this.setMapupdateflags({
          [flagToSetDirty]: true,
        })
      }
    },
  },
})
